/**
 * useCart Hook
 *
 * Typed React hooks for accessing the cart store.
 * Similar to use-enrollment.ts but for cart operations.
 */
import { useSelect, useDispatch } from '@wordpress/data';
import type { StoreAPICartResponse, CartCoupon } from '~/shared/queries/wc-store-api/types';
import { CART_STORE_NAME } from '../store/cart-store';
import type { CartActions, CartSelectors } from '../store/cart-store';

// ============================================================================
// Types
// ============================================================================

interface CartData {
	cart: StoreAPICartResponse | null;
	coupons: CartCoupon[];
	isCouponApplied: (code: string) => boolean;
	itemsCount: number;
	isLoading: boolean;
	error: string | null;
	hasCart: boolean;
}

interface CartActionsHook {
	setCart: (cart: StoreAPICartResponse | null) => void;
	refetchCart: () => void;
	setLoading: (isLoading: boolean) => void;
	setError: (error: string | null) => void;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access cart data from the cart store
 *
 * @returns Cart data and helper methods
 *
 * @example
 * ```tsx
 * function MyCouponCard() {
 *   const { cart, isCouponApplied, refetchCart } = useCart();
 *
 *   const isApplied = isCouponApplied('SUMMER2024');
 *
 *   return <div>Coupon applied: {isApplied ? 'Yes' : 'No'}</div>;
 * }
 * ```
 */
export function useCart(): CartData {
	const cart = useSelect(
		(select) => (select(CART_STORE_NAME) as CartSelectors).getCart(),
		[]
	);

	const coupons = useSelect(
		(select) => (select(CART_STORE_NAME) as CartSelectors).getCoupons(),
		[]
	);

	const isLoading = useSelect(
		(select) => (select(CART_STORE_NAME) as CartSelectors).isLoading(),
		[]
	);

	const error = useSelect(
		(select) => (select(CART_STORE_NAME) as CartSelectors).getError(),
		[]
	);

	const itemsCount = useSelect(
		(select) => (select(CART_STORE_NAME) as CartSelectors).getCartItemsCount(),
		[]
	);

	// Helper function to check if a specific coupon is applied
	const isCouponApplied = (code: string): boolean => {
		return cart?.coupons?.some((c) => c.code === code) ?? false;
	};

	return {
		cart,
		coupons,
		isCouponApplied,
		itemsCount,
		isLoading,
		error,
		hasCart: cart !== null,
	};
}

/**
 * Hook to access cart store actions
 *
 * @returns Cart store actions
 *
 * @example
 * ```tsx
 * function MyCouponCard() {
 *   const { refetchCart } = useCartActions();
 *
 *   const handleApplyCoupon = async () => {
 *     await applyCoupon('SUMMER2024');
 *     refetchCart(); // Refresh cart to show updated state
 *   };
 *
 *   return <button onClick={handleApplyCoupon}>Apply</button>;
 * }
 * ```
 */
export function useCartActions(): CartActionsHook {
	const { setCart, refetchCart, setLoading, setError } = useDispatch(
		CART_STORE_NAME
	) as CartActions;

	return {
		setCart,
		refetchCart,
		setLoading,
		setError,
	};
}
